import { useRef } from 'react';
import styles from './CodeBoxes.module.css';

/**
 * Six independent text inputs for a 6-digit code:
 *  - Auto-advance on entry
 *  - Backspace moves back
 *  - Arrow keys navigate
 *  - Paste of "123456" fills all boxes at once
 *  - Calls onComplete(code) once all 6 digits are filled
 */
export default function CodeBoxes({ value, onChange, onComplete, disabled, length = 6 }) {
  const refs = useRef([]);
  const digits = value.padEnd(length, ' ').split('').slice(0, length);

  const setDigit = (i, d) => {
    const cleaned = d.replace(/\D/g, '').slice(-1);
    const newDigits = digits.slice();
    newDigits[i] = cleaned || ' ';
    const newVal = newDigits.join('').replace(/\s+$/, '');
    onChange(newVal);
    if (cleaned && i < length - 1) refs.current[i + 1]?.focus();
    if (cleaned && i === length - 1 && newVal.replace(/\s/g, '').length === length) {
      onComplete?.(newVal);
    }
  };

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i].trim() && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    onChange(pasted);
    if (pasted.length === length) {
      refs.current[length - 1]?.focus();
      onComplete?.(pasted);
    } else {
      refs.current[pasted.length]?.focus();
    }
  };

  return (
    <div className={styles.codeRow}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={styles.codeBox}
          value={d.trim()}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${length}`}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}
